import React, { useEffect, useRef, useState } from "react";
import { FlaskConical, Loader2, RefreshCcw } from "lucide-react";
import { ClassMove, TransitionChips, SearchBox, Pager, useDebounced, cls } from "../ChangeParts";
import { whatIf } from "../../../lib/classificationApi";
import { formatPeso, formatNumber } from "../../../lib/utils";

const CELLS = ["AX", "AY", "AZ", "BX", "BY", "BZ", "CX", "CY", "CZ", "ZI"];

function ssText(ss) {
  const parts = [];
  if (ss.skus) parts.push(`${formatNumber(ss.skus)} SKUs · median ${ss.median_change_days > 0 ? "+" : ""}${ss.median_change_days} d`);
  if (ss.lose_formula) parts.push(`${formatNumber(ss.lose_formula)} lose formula buffer`);
  if (ss.gain_formula) parts.push(`${formatNumber(ss.gain_formula)} gain one`);
  return parts.join(" · ") || "none";
}

function Kpis({ d }) {
  const ss = d.safety_stock;
  const items = [
    ["SKUs changing class", formatNumber(d.movers_total), "whatif-movers"],
    ["Annual value of those SKUs", formatPeso(d.value_moved, { compact: true }), "whatif-value"],
    ["Into / out of Z-Intermittent", `${formatNumber(d.into_zi)} / ${formatNumber(d.out_of_zi)}`, "whatif-zi"],
    ["Safety stock changes", ssText(ss), "whatif-ss"],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border border-b border-border">
      {items.map(([l, v, t]) => (
        <div key={t} className="px-3 py-2">
          <div className="text-[9.5px] uppercase tracking-widest text-subtle">{l}</div>
          <div data-testid={t} className="font-mono-tabular text-[13px] font-semibold mt-0.5">{v}</div>
        </div>
      ))}
    </div>
  );
}

function CellDelta({ before, after }) {
  const moved = CELLS.filter(c => before[c].count !== after[c].count);
  if (!moved.length) return null;
  return (
    <div data-testid="whatif-cells" className="flex flex-wrap gap-x-3 gap-y-1 text-[10.5px] font-mono-tabular text-muted">
      {moved.map(c => (
        <span key={c}><strong className="text-fg">{cls(c)}</strong> {formatNumber(before[c].count)} → <span className="text-primary">{formatNumber(after[c].count)}</span></span>
      ))}
    </div>
  );
}

function Movers({ rows }) {
  return (
    <div className="max-h-[280px] overflow-auto">
      <table className="w-full text-[11.5px]">
        <thead className="sticky top-0 bg-surface">
          <tr className="text-[9.5px] uppercase tracking-widest text-subtle border-b border-border">
            {["SKU", "Class", "Annual value", "Why it moves"].map((h, i) => <th key={h} className={`text-left font-semibold px-3 py-1.5 ${i > 1 ? "hidden md:table-cell" : ""}`}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.sku} data-testid={`whatif-row-${r.sku}`} className="border-b border-border/60 align-top">
              <td className="px-3 py-1.5">
                <div className="font-mono-tabular">{r.sku}</div><div className="text-[10.5px] text-muted truncate max-w-[200px]">{r.name}</div>
                <div className="md:hidden text-[10px] text-muted leading-snug mt-0.5">{formatPeso(r.value_after, { compact: true })} · {r.drivers[0]}</div>
              </td>
              <td className="px-3 py-1.5"><ClassMove from={r.from} to={r.to} /></td>
              <td className="hidden md:table-cell px-3 py-1.5 font-mono-tabular whitespace-nowrap">{formatPeso(r.value_after, { compact: true })}</td>
              <td className="hidden md:table-cell px-3 py-1.5 text-[10.5px] text-muted leading-snug min-w-[220px]">{r.drivers[0]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Rule What-If: re-classifies with the candidate value (nothing saved) and lists every SKU that would change class.
export default function WhatIfPreview({ setting, value, actor, onReady }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [transition, setTransition] = useState(null);
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);
  const ready = useRef(onReady);
  ready.current = onReady;

  useEffect(() => { setPage(1); }, [dq, transition]);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    whatIf({ key: setting.key, value, q: dq || undefined, transition: transition || undefined, page, size: 50 }, actor)
      .then(x => { if (alive) { setD(x); setError(null); ready.current(true); } })
      .catch(e => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [setting.key, value, actor, dq, transition, page, nonce]);

  return (
    <div data-testid="whatif" className="border border-primary/30 rounded-sm bg-surface-2/20">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border text-[10.5px] uppercase tracking-widest font-semibold text-primary">
        <FlaskConical size={12} /> What-if preview · nothing is saved yet
        {loading && <Loader2 size={12} className="animate-spin text-muted ml-auto" data-testid="whatif-loading" />}
      </div>
      {!d && loading && <div className="px-3 py-6 text-center text-[11.5px] text-muted">Re-classifying every SKU with the new value…</div>}
      {error && (
        <div data-testid="whatif-error" className="m-3 text-[11.5px] text-critical border border-critical/30 bg-critical/5 rounded-sm p-2.5 flex items-start gap-2">
          <span className="flex-1">{error}</span>
          <button type="button" data-testid="whatif-retry" onClick={() => setNonce(n => n + 1)} className="inline-flex items-center gap-1 text-[11px] underline"><RefreshCcw size={11} /> Retry</button>
        </div>
      )}
      {d && (
        <>
          <Kpis d={d} />
          <div className="px-3 py-2.5 space-y-2">
            <CellDelta before={d.cells_before} after={d.cells_after} />
            {d.movers_total === 0 ? (
              <div data-testid="whatif-none" className="text-[11.5px] text-muted">No SKU changes class at this value{d.safety_stock.skus || d.replacement_changed ? " — only safety-stock and replacement figures change." : "."}</div>
            ) : (
              <>
                <TransitionChips transitions={d.transitions} active={transition} onPick={setTransition} testid="whatif-transition" />
                <SearchBox value={q} onChange={setQ} testid="whatif-search" />
              </>
            )}
          </div>
          {d.movers_total > 0 && (
            <>
              <Movers rows={d.rows} />
              {d.rows.length === 0 && <div data-testid="whatif-empty" className="py-4 text-center text-[11.5px] text-muted">No moving SKU matches “{dq}”.</div>}
              <Pager data={d} onPage={setPage} testid="whatif-page" />
            </>
          )}
        </>
      )}
    </div>
  );
}
